import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { NotificationProvider } from './contexts/NotificationContext';
import { CreditProvider } from './contexts/CreditContext';
import { MediaEntitlementProvider } from './contexts/MediaEntitlementContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { ProductGuideProvider } from './contexts/ProductGuideContext';
import { ContentProtectionWrapper } from './components/ContentProtectionWrapper';
import GlobalUserBar from './components/GlobalUserBar';
import { GlobalBackButton } from './components/BackButton';
import { ErrorBoundary } from './components/recovery';
import AppTour, { TourProvider } from './components/AppTour';
import { initActivationSentinel } from './utils/activationSentinel';
import { safeRedirectPath } from './utils/safeRedirect';
import CookieConsent from './components/CookieConsent';
// P0 UI Cleanup (Apr 2026): floating support clutter removed by founder directive.
// PushPrompt / GuideAssistant / ResponsiveSupportWrapper imports retired.
// One manual-trigger drawer remains via ActionGuideMount.
import useSessionTracker from './utils/useSessionTracker';
import { PurchaseSurveyMount } from './pages/PurchaseSurvey';
import { ActionGuideMount } from './utils/ActionGuide';
import SubscribeRequiredModal from './components/SubscribeRequiredModal';
import './App.css';

// ═══ CRITICAL PATH — Eager imports (landing, auth, dashboard) ═══
import Landing from './pages/Landing';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import AuthCallback from './pages/AuthCallback';

// ═══ LIGHTWEIGHT — Small components always needed ═══
import JourneyProgressBar from './components/guide/JourneyProgressBar';
import FirstActionOverlay from './components/guide/FirstActionOverlay';
import PostValueOverlay from './components/guide/PostValueOverlay';
import { UpgradeModal } from './components/UpgradeModal';
// Removed Apr 26 2026 (P0 UI cleanup): ResponsiveSupportWrapper, GuideAssistant.
// Floating support widgets / launchers cause clutter and reduce premium feel.
// Replacement is contextual inline help via ActionGuide.

// ═══ ROUTE-LEVEL LAZY LOADING — Everything else loaded on demand ═══
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="text-center">
      <div className="relative w-8 h-8 mx-auto mb-3">
        <div className="absolute inset-0 rounded-full border-2 border-slate-700/50" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 animate-spin" />
      </div>
      <p className="text-slate-500 text-xs">Loading...</p>
    </div>
  </div>
);

// Public pages
const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const Reviews = lazy(() => import('./pages/Reviews'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const SecurityReportPage = lazy(() => import('./pages/SecurityReportPage'));
const SecurityReportSubmittedPage = lazy(() => import('./pages/SecurityReportSubmittedPage'));
const AdminSecurityReports = lazy(() => import('./pages/AdminSecurityReports'));
const AdminSecurityReportDetail = lazy(() => import('./pages/AdminSecurityReportDetail'));
const ReferLanding = lazy(() => import('./pages/ReferLanding'));
const ReferralsPage = lazy(() => import('./pages/ReferralsPage'));
const AdminReferrals = lazy(() => import('./pages/AdminReferrals'));
const AdminReactions = lazy(() => import('./pages/AdminReactions'));
const AdminActivation = lazy(() => import('./pages/AdminActivation'));
const AdminShareLinks = lazy(() => import('./pages/AdminShareLinks'));
const Blog = lazy(() => import('./pages/Blog'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const SharePage = lazy(() => import('./pages/SharePage'));
const ViralPackShare = lazy(() => import('./pages/ViralPackShare'));
const Gallery = lazy(() => import('./pages/Gallery'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const PublicCreation = lazy(() => import('./pages/PublicCreation'));
const CreatorProfile = lazy(() => import('./pages/CreatorProfile'));
const PublicCharacterPage = lazy(() => import('./pages/PublicCharacterPage'));
const PublicSeries = lazy(() => import('./pages/PublicSeries'));
const InstantStoryExperience = lazy(() => import('./pages/InstantStoryExperience'));
const UserManual = lazy(() => import('./pages/UserManual'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

// Core app pages
const StoryVideoPipeline = lazy(() => import('./pages/StoryVideoPipeline'));
const StoryPreview = lazy(() => import('./pages/StoryPreview'));
const StoryBattlePage = lazy(() => import('./pages/StoryBattlePage'));
const StoryViewerPage = lazy(() => import('./pages/StoryViewerPage'));
const StoryChainView = lazy(() => import('./pages/StoryChainView'));
const StoryChainTimeline = lazy(() => import('./pages/StoryChainTimeline'));
const DailyWarPage = lazy(() => import('./pages/DailyWarPage'));
const MyStories = lazy(() => import('./pages/MyStories'));

// Creator tools
const ReelGenerator = lazy(() => import('./pages/ReelGenerator'));
const StoryGenerator = lazy(() => import('./pages/StoryGenerator'));
const ComicStorybookBuilder = lazy(() => import('./pages/ComicStorybookBuilder'));
const PhotoToComic = lazy(() => import('./pages/PhotoToComic'));
const PhotoReactionGIF = lazy(() => import('./pages/PhotoReactionGIF'));
const GifMaker = lazy(() => import('./pages/GifMaker'));
const ComicStorybook = lazy(() => import('./pages/ComicStorybook'));
const CharacterConsistencyStudio = lazy(() => import('./pages/CharacterConsistencyStudio'));
const ColoringBookWizard = lazy(() => import('./pages/ColoringBookWizard'));
const BedtimeStoryBuilder = lazy(() => import('./pages/BedtimeStoryBuilder'));
const StoryEpisodeCreator = lazy(() => import('./pages/StoryEpisodeCreator'));
const ContentChallengePlanner = lazy(() => import('./pages/ContentChallengePlanner'));
const CaptionRewriterPro = lazy(() => import('./pages/CaptionRewriterPro'));
const PromoVideos = lazy(() => import('./pages/PromoVideos'));
const PhotoTrailerPage = lazy(() => import('./pages/PhotoTrailerPage'));
const CreateMenuPage = lazy(() => import('./pages/CreateMenuPage'));
const PublicTrailerPage = lazy(() => import('./pages/PublicTrailerPage'));

// Navigation / user pages
const MySpacePage = lazy(() => import('./pages/MySpacePage'));
const BrowsePage = lazy(() => import('./pages/BrowsePage'));
const CharactersPage = lazy(() => import('./pages/CharactersPage'));
const UserDashboardPage = lazy(() => import('./pages/UserDashboardPage'));
const History = lazy(() => import('./pages/History'));
const Billing = lazy(() => import('./pages/Billing'));
const Profile = lazy(() => import('./pages/Profile'));
const PrivacySettings = lazy(() => import('./pages/PrivacySettings'));
const PublicPrivacySettings = lazy(() => import('./pages/PublicPrivacySettings'));
const CopyrightInfo = lazy(() => import('./pages/CopyrightInfo'));
const CreatorTools = lazy(() => import('./pages/CreatorTools'));
const PaymentHistory = lazy(() => import('./pages/PaymentHistory'));
const FeatureRequests = lazy(() => import('./pages/FeatureRequests'));
const ReferralProgram = lazy(() => import('./pages/ReferralProgram'));
const MyDownloads = lazy(() => import('./pages/MyDownloads'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const SubscriptionManagement = lazy(() => import('./pages/SubscriptionManagement.jsx'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));

// Series / Characters
const StorySeries = lazy(() => import('./pages/StorySeries'));
const CreateSeries = lazy(() => import('./pages/CreateSeries'));
const SeriesTimeline = lazy(() => import('./pages/SeriesTimeline'));
const CharacterCreator = lazy(() => import('./pages/CharacterCreator'));
const CharacterLibrary = lazy(() => import('./pages/CharacterLibrary'));
const CharacterDetail = lazy(() => import('./pages/CharacterDetail'));

// Template features
const CreatorProTools = lazy(() => import('./pages/CreatorProTools'));
const TwinFinder = lazy(() => import('./pages/TwinFinder'));
const ChallengeGenerator = lazy(() => import('./pages/ChallengeGenerator'));
const ToneSwitcher = lazy(() => import('./pages/ToneSwitcher'));
const ContentBlueprintLibrary = lazy(() => import('./pages/ContentBlueprintLibrary'));
const InstagramBioGenerator = lazy(() => import('./pages/InstagramBioGenerator'));
const CommentReplyBank = lazy(() => import('./pages/CommentReplyBank'));
const YouTubeThumbnailGenerator = lazy(() => import('./pages/YouTubeThumbnailGenerator'));
const BrandStoryBuilder = lazy(() => import('./pages/BrandStoryBuilder'));
const OfferGenerator = lazy(() => import('./pages/OfferGenerator'));
const StoryHookGenerator = lazy(() => import('./pages/StoryHookGenerator'));
const DailyViralIdeas = lazy(() => import('./pages/DailyViralIdeas'));
const ConversionDashboard = lazy(() => import('./pages/ConversionDashboard'));
const ContentEngine = lazy(() => import('./pages/ContentEngine'));

// Admin — entire admin chunk lazy loaded
const AdminLayout = lazy(() => import('./components/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const RetentionDashboard = lazy(() => import('./pages/RetentionDashboard'));
const AutomationDashboard = lazy(() => import('./pages/AutomationDashboard'));
const RealtimeAnalytics = lazy(() => import('./pages/RealtimeAnalytics'));
const AdminMonitoring = lazy(() => import('./pages/AdminMonitoring'));
const AdminLoginActivity = lazy(() => import('./pages/AdminLoginActivity'));
const AdminUsersManagement = lazy(() => import('./pages/AdminUsersManagement'));
const StoryVideoAnalyticsDashboard = lazy(() => import('./pages/StoryVideoAnalyticsDashboard'));
const SelfHealingDashboard = lazy(() => import('./pages/Admin/SelfHealingDashboard'));
const UserAnalyticsDashboard = lazy(() => import('./pages/Admin/UserAnalyticsDashboard'));
const AdminSecurityDashboard = lazy(() => import('./pages/AdminSecurityDashboard'));
const BioTemplatesAdmin = lazy(() => import('./pages/Admin/BioTemplatesAdmin'));
const WorkerDashboard = lazy(() => import('./pages/admin/WorkerDashboard'));
const ActivationDiagnostics = lazy(() => import('./pages/Admin/ActivationDiagnostics'));
const TTFDDashboard = lazy(() => import('./pages/admin/TTFDDashboard'));
const PaymentsDashboard = lazy(() => import('./pages/admin/PaymentsDashboard'));
const TemplateAnalyticsDashboard = lazy(() => import('./pages/Admin/TemplateAnalyticsDashboard'));
const AdminAuditLogs = lazy(() => import('./pages/Admin/AuditLogs'));
const TemplateLeaderboard = lazy(() => import('./pages/Admin/TemplateLeaderboard'));
const DailyReportDashboard = lazy(() => import('./pages/Admin/DailyReportDashboard'));
const AccountLockManagement = lazy(() => import('./pages/Admin/AccountLockManagement'));
const EnvironmentMonitor = lazy(() => import('./pages/Admin/EnvironmentMonitor'));
const UserActivityDashboard = lazy(() => import('./pages/Admin/UserActivityDashboard'));
const SystemHealthDashboard = lazy(() => import('./pages/Admin/SystemHealthDashboard'));
const AntiAbuseDashboard = lazy(() => import('./pages/Admin/AntiAbuseDashboard'));
const RevenueAnalyticsDashboard = lazy(() => import('./pages/Admin/RevenueAnalyticsDashboard'));
const MonitoringDashboard = lazy(() => import('./pages/Admin/MonitoringDashboard'));
const GA4EventTester = lazy(() => import('./pages/Admin/GA4EventTester'));
const AdminFeedbackPage = lazy(() => import('./pages/Admin/AdminFeedbackPage'));
const GrowthDashboard = lazy(() => import('./pages/Admin/GrowthDashboard'));
const ProductionMetrics = lazy(() => import('./pages/Admin/ProductionMetrics'));
const MediaSecurityDashboard = lazy(() => import('./pages/Admin/MediaSecurityDashboard'));
const PhotoTrailerKpiDashboard = lazy(() => import('./pages/Admin/PhotoTrailerKpiDashboard'));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

/** Protected route wrapper */
function ProtectedRoute({ auth, children }) {
  const loc = useLocation();
  if (!auth) {
    if (loc.pathname && loc.pathname !== '/login' && loc.pathname !== '/signup') {
      localStorage.setItem('auth_return_path', loc.pathname);
    }
    return <LoginRedirect />;
  }
  return children;
}

/**
 * P0 2026-06 — Deep-link-preserving login redirect.
 *
 * Replaces every plain login-Navigate so that anonymous deep-link
 * visits to `/app/<route>` round-trip cleanly: user → /login?next=<path>
 * → sign-in → original route. Previously, ~80 protected routes silently
 * dropped the destination, which was a measurable funnel leak (paid
 * users clicking email/share links lost context).
 *
 * Param contract: canonical `?next=<encoded-path>`. Login.js also accepts
 * legacy `?return=`. Pinned by
 * tests/test_protected_route_next_redirect_2026_06.py.
 */
function LoginRedirect() {
  const loc = useLocation();
  const path = (loc.pathname || '') + (loc.search || '');
  // Never preserve `/`, `/login`, or `/signup` — those are not
  // meaningful return destinations and would cause loops.
  const safe = path && path !== '/' && path !== '/login' && path !== '/signup';
  const target = safe
    ? `/login?next=${encodeURIComponent(path)}`
    : '/login';
  return <Navigate to={target} replace />;
}

function AuthenticatedRedirect() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  // P0 2026-06 — accept canonical `?next=` first, then legacy `?return=`.
  // P0 2026-06 SECURITY — sanitize against open-redirect attacks.
  // Pinned: tests/test_safe_redirect_open_redirect_guard_2026_06.py
  const rawParam =
    searchParams.get('next') ||
    searchParams.get('return') ||
    localStorage.getItem('remix_return_url');
  if (rawParam && localStorage.getItem('remix_return_url')) {
    localStorage.removeItem('remix_return_url');
  }
  const returnUrl = rawParam ? safeRedirectPath(rawParam) : '/app';
  return <Navigate to={returnUrl} replace />;
}

/** Lazy wrapper with suspense fallback */
function L({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState('post_value');

  // Session tracking — fires session_started / session_ended
  useSessionTracker();

  const triggerPaywall = useCallback((reason = 'post_value') => {
    setPaywallReason(reason);
    setPaywallOpen(true);
  }, []);

  useEffect(() => {
    const handler = (e) => triggerPaywall(e.detail?.reason || 'exit_interception');
    window.addEventListener('trigger-paywall', handler);
    return () => window.removeEventListener('trigger-paywall', handler);
  }, [triggerPaywall]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) setIsAuthenticated(true);
    setLoading(false);
    // P0 activation sentinel — global error & UX failure detector
    try { initActivationSentinel(); } catch (_) { /* never block app boot */ }
    // P0 2026-05-22 Phase A — Attribution capture on first page load.
    // Captures gclid/gbraid/wbraid/fbclid/utm_* from the URL, persists
    // for 90 days, and best-effort POSTs to the backend so the
    // cashfree webhook can stamp the order at payment time. Wrapped
    // in try/catch — attribution must NEVER block app boot.
    try {
      // Lazy imports so missing modules cannot break the bundle.
      // eslint-disable-next-line global-require
      const { captureAttribution, syncAttributionToBackend } = require('./utils/attribution');
      // eslint-disable-next-line global-require
      const { configureGoogleAdsTag } = require('./utils/googleAdsConversions');
      // eslint-disable-next-line global-require
      const api = require('./utils/api').default;
      captureAttribution();
      syncAttributionToBackend(api);
      configureGoogleAdsTag();
    } catch (_) { /* never block app boot */ }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'linear-gradient(135deg, #0a0e1a 0%, #111827 40%, #0f172a 100%)' }}>
        <div className="text-center">
          <div className="relative w-10 h-10 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-2 border-slate-700/50" />
            <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-indigo-500 animate-spin" />
          </div>
          <p className="text-slate-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <NotificationProvider>
    <CreditProvider>
    <MediaEntitlementProvider>
    <TourProvider>
    <FeedbackProvider>
    <ProductGuideProvider>
    <ContentProtectionWrapper>
      {isAuthenticated && (() => {
        try {
          const t = localStorage.getItem('token');
          if (t) {
            const p = JSON.parse(atob(t.split('.')[1]));
            if (p.role?.toUpperCase() === 'ADMIN' || p.role?.toUpperCase() === 'SUPERADMIN') return null;
          }
        } catch {}
        const path = window.location.pathname;
        if (path.includes('story-video-studio') || path.includes('character-consistency')) {
          return <JourneyProgressBar />;
        }
        return null;
      })()}
      {isAuthenticated && <GlobalUserBar />}
      <PurchaseSurveyMount />
      <ActionGuideMount />
      <SubscribeRequiredModal />
      <GlobalBackButton />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* ═══ PUBLIC — Eager loaded ═══ */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={isAuthenticated ? <AuthenticatedRedirect /> : <Login setAuth={setIsAuthenticated} />} />
        <Route path="/signup" element={isAuthenticated ? <AuthenticatedRedirect /> : <Signup setAuth={setIsAuthenticated} />} />
        <Route path="/auth/callback" element={<AuthCallback setAuth={setIsAuthenticated} />} />

        {/* ═══ PUBLIC — Lazy loaded ═══ */}
        <Route path="/pricing" element={<L><Pricing /></L>} />
        <Route path="/contact" element={<L><Contact /></L>} />
        <Route path="/reviews" element={<L><Reviews /></L>} />
        <Route path="/about" element={<L><AboutPage /></L>} />
        <Route path="/blog" element={<L><Blog /></L>} />
        <Route path="/blog/:slug" element={<L><Blog /></L>} />
        <Route path="/verify-email" element={<L><VerifyEmail /></L>} />
        <Route path="/reset-password" element={<L><ResetPassword /></L>} />
        <Route path="/forgot-password" element={<L><ForgotPassword /></L>} />
        <Route path="/share/:shareId" element={<L><SharePage /></L>} />
        <Route path="/viral/:jobId" element={<L><ViralPackShare /></L>} />
        <Route path="/gallery" element={<L><Gallery /></L>} />
        <Route path="/explore" element={<L><ExplorePage /></L>} />
        <Route path="/app/explore" element={<L><ExplorePage /></L>} />
        <Route path="/v/:slug" element={<L><PublicCreation /></L>} />
        <Route path="/character/:characterId" element={<L><PublicCharacterPage /></L>} />
        <Route path="/creator/:username" element={<L><CreatorProfile /></L>} />
        <Route path="/series/:seriesId" element={<L><PublicSeries /></L>} />
        <Route path="/experience" element={<L><InstantStoryExperience /></L>} />
        <Route path="/user-manual" element={<L><UserManual /></L>} />
        <Route path="/help" element={<L><UserManual /></L>} />
        <Route path="/privacy-policy" element={<L><PrivacyPolicy /></L>} />
        <Route path="/cookie-policy" element={<L><CookiePolicy /></L>} />
        <Route path="/privacy-settings" element={<L><PublicPrivacySettings /></L>} />
        <Route path="/terms" element={<L><TermsOfService /></L>} />
        <Route path="/terms-of-service" element={<L><TermsOfService /></L>} />

        {/* ═══ SECURITY / VDP ═══ */}
        <Route path="/security" element={<L><SecurityPage /></L>} />
        <Route path="/security/report" element={<L><SecurityReportPage /></L>} />
        <Route path="/security/report/submitted" element={<L><SecurityReportSubmittedPage /></L>} />

        {/* ═══ REFERRALS ═══ */}
        <Route path="/refer" element={<L><ReferLanding /></L>} />

        {/* ═══ CORE APP — Dashboard eager, rest lazy ═══ */}
        <Route path="/app" element={isAuthenticated ? <Dashboard /> : <LoginRedirect />} />
        <Route path="/app/story-video-studio" element={<L><ErrorBoundary><StoryVideoPipeline /></ErrorBoundary></L>} />
        <Route path="/app/story-preview/:jobId" element={<L><StoryPreview /></L>} />
        <Route path="/app/story-battle/:storyId" element={isAuthenticated ? <L><StoryBattlePage /></L> : <LoginRedirect />} />
        <Route path="/app/story-viewer/:jobId" element={isAuthenticated ? <L><StoryViewerPage /></L> : <LoginRedirect />} />
        <Route path="/app/story-chain/:chainId" element={isAuthenticated ? <L><StoryChainView /></L> : <LoginRedirect />} />
        <Route path="/app/story-chain-timeline/:storyId" element={isAuthenticated ? <L><StoryChainTimeline /></L> : <LoginRedirect />} />
        <Route path="/app/war" element={isAuthenticated ? <L><DailyWarPage /></L> : <LoginRedirect />} />
        <Route path="/app/my-stories" element={isAuthenticated ? <L><MyStories /></L> : <LoginRedirect />} />

        {/* ═══ NAVIGATION PAGES ═══ */}
        <Route path="/app/my-space" element={isAuthenticated ? <L><MySpacePage /></L> : <LoginRedirect />} />
        <Route path="/app/my-space/:assetId" element={isAuthenticated ? <L><MySpacePage /></L> : <LoginRedirect />} />
        {/* /app/create routes to CreateMenuPage (see line below) — old CreatePage removed 2026-05-03 */}
        <Route path="/app/browse" element={isAuthenticated ? <L><BrowsePage /></L> : <LoginRedirect />} />
        <Route path="/app/characters" element={isAuthenticated ? <L><CharacterLibrary /></L> : <LoginRedirect />} />
        <Route path="/app/dashboard" element={isAuthenticated ? <L><UserDashboardPage /></L> : <LoginRedirect />} />
        <Route path="/app/referrals" element={isAuthenticated ? <L><ReferralsPage /></L> : <LoginRedirect />} />
        <Route path="/dashboard/referrals" element={isAuthenticated ? <L><ReferralsPage /></L> : <LoginRedirect />} />

        {/* ═══ CREATOR TOOLS ═══ */}
        <Route path="/app/reels" element={isAuthenticated ? <L><ReelGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/reel-generator" element={isAuthenticated ? <L><ReelGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/reel" element={isAuthenticated ? <Navigate to="/app/reel-generator" /> : <LoginRedirect />} />
        <Route path="/app/stories" element={isAuthenticated ? <L><StoryGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/kids-story" element={isAuthenticated ? <L><StoryGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/story-generator" element={isAuthenticated ? <L><StoryGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/story" element={isAuthenticated ? <Navigate to="/app/story-generator" /> : <LoginRedirect />} />
        <Route path="/app/story-pack" element={isAuthenticated ? <Navigate to="/app/story-generator" /> : <LoginRedirect />} />
        <Route path="/app/character-studio" element={isAuthenticated ? <L><CharacterConsistencyStudio /></L> : <LoginRedirect />} />
        <Route path="/app/story-video-studio/characters" element={isAuthenticated ? <L><CharacterConsistencyStudio /></L> : <LoginRedirect />} />
        <Route path="/app/coloring-book" element={isAuthenticated ? <L><ColoringBookWizard /></L> : <LoginRedirect />} />
        <Route path="/app/comic" element={isAuthenticated ? <L><PhotoToComic /></L> : <LoginRedirect />} />
        <Route path="/app/comix" element={isAuthenticated ? <L><PhotoToComic /></L> : <LoginRedirect />} />
        <Route path="/app/comix-ai" element={isAuthenticated ? <L><PhotoToComic /></L> : <LoginRedirect />} />
        <Route path="/app/photo-to-comic" element={isAuthenticated ? <L><PhotoToComic /></L> : <LoginRedirect />} />
        <Route path="/app/gif-maker" element={isAuthenticated ? <L><PhotoReactionGIF /></L> : <LoginRedirect />} />
        <Route path="/app/reaction-gif" element={isAuthenticated ? <L><PhotoReactionGIF /></L> : <LoginRedirect />} />
        <Route path="/app/gif-maker-old" element={isAuthenticated ? <L><GifMaker /></L> : <LoginRedirect />} />
        <Route path="/app/comic-storybook" element={isAuthenticated ? <L><ComicStorybookBuilder /></L> : <LoginRedirect />} />
        <Route path="/app/comic-story-builder" element={isAuthenticated ? <L><ComicStorybookBuilder /></L> : <LoginRedirect />} />
        <Route path="/app/comic-storybook-old" element={isAuthenticated ? <L><ComicStorybook /></L> : <LoginRedirect />} />
        <Route path="/app/bedtime-story-builder" element={isAuthenticated ? <L><BedtimeStoryBuilder /></L> : <LoginRedirect />} />
        <Route path="/app/bedtime-stories" element={isAuthenticated ? <L><BedtimeStoryBuilder /></L> : <LoginRedirect />} />
        <Route path="/app/story-episode-creator" element={isAuthenticated ? <L><StoryEpisodeCreator /></L> : <LoginRedirect />} />
        <Route path="/app/content-challenge-planner" element={isAuthenticated ? <L><ContentChallengePlanner /></L> : <LoginRedirect />} />
        <Route path="/app/caption-rewriter" element={isAuthenticated ? <L><CaptionRewriterPro /></L> : <LoginRedirect />} />
        <Route path="/app/promo-videos" element={isAuthenticated ? <L><PromoVideos /></L> : <LoginRedirect />} />
        <Route path="/app/photo-trailer" element={isAuthenticated ? <L><PhotoTrailerPage /></L> : <LoginRedirect />} />
        <Route path="/app/create" element={isAuthenticated ? <L><CreateMenuPage /></L> : <LoginRedirect />} />
        <Route path="/app/youstar" element={isAuthenticated ? <L><PhotoTrailerPage /></L> : <LoginRedirect />} />
        <Route path="/app/my-movie-trailer" element={isAuthenticated ? <L><PhotoTrailerPage /></L> : <LoginRedirect />} />
        {/* Public share page — no auth, slug is the access token */}
        <Route path="/trailer/:slug" element={<L><PublicTrailerPage /></L>} />

        {/* ═══ USER PAGES ═══ */}
        <Route path="/app/history" element={isAuthenticated ? <L><History /></L> : <LoginRedirect />} />
        <Route path="/app/billing" element={isAuthenticated ? <L><Billing /></L> : <LoginRedirect />} />
        <Route path="/app/profile" element={isAuthenticated ? <L><Profile /></L> : <LoginRedirect />} />
        <Route path="/app/privacy" element={isAuthenticated ? <L><PrivacySettings /></L> : <LoginRedirect />} />
        <Route path="/app/copyright" element={isAuthenticated ? <L><CopyrightInfo /></L> : <LoginRedirect />} />
        <Route path="/app/creator-tools" element={isAuthenticated ? <L><CreatorTools /></L> : <LoginRedirect />} />
        <Route path="/app/content-vault" element={<Navigate to="/app/blueprint-library" replace />} />
        <Route path="/app/blueprint-library" element={isAuthenticated ? <L><ContentBlueprintLibrary /></L> : <LoginRedirect />} />
        <Route path="/app/payment-history" element={isAuthenticated ? <L><PaymentHistory /></L> : <LoginRedirect />} />
        <Route path="/app/feature-requests" element={isAuthenticated ? <L><FeatureRequests /></L> : <LoginRedirect />} />
        <Route path="/app/referral" element={isAuthenticated ? <L><ReferralProgram /></L> : <LoginRedirect />} />
        <Route path="/app/gift-cards" element={isAuthenticated ? <L><ReferralProgram /></L> : <LoginRedirect />} />
        <Route path="/app/downloads" element={isAuthenticated ? <L><MyDownloads /></L> : <LoginRedirect />} />
        <Route path="/app/my-downloads" element={isAuthenticated ? <L><MyDownloads /></L> : <LoginRedirect />} />
        <Route path="/app/pricing" element={isAuthenticated ? <L><PricingPage /></L> : <LoginRedirect />} />
        <Route path="/app/subscription" element={isAuthenticated ? <L><SubscriptionManagement /></L> : <LoginRedirect />} />
        <Route path="/app/analytics" element={isAuthenticated ? <L><AnalyticsDashboard /></L> : <LoginRedirect />} />

        {/* ═══ SERIES / CHARACTERS ═══ */}
        <Route path="/app/story-series" element={isAuthenticated ? <L><StorySeries /></L> : <LoginRedirect />} />
        <Route path="/app/story-series/create" element={isAuthenticated ? <L><CreateSeries /></L> : <LoginRedirect />} />
        <Route path="/app/story-series/:seriesId" element={<L><SeriesTimeline /></L>} />
        <Route path="/app/characters/create" element={isAuthenticated ? <L><CharacterCreator /></L> : <LoginRedirect />} />
        <Route path="/app/characters/:characterId" element={isAuthenticated ? <L><CharacterDetail /></L> : <LoginRedirect />} />

        {/* ═══ TEMPLATE FEATURES ═══ */}
        <Route path="/app/creator-pro" element={isAuthenticated ? <L><CreatorProTools /></L> : <LoginRedirect />} />
        <Route path="/app/twinfinder" element={isAuthenticated ? <L><TwinFinder /></L> : <LoginRedirect />} />
        <Route path="/app/challenge-generator" element={isAuthenticated ? <L><ChallengeGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/tone-switcher" element={isAuthenticated ? <L><ToneSwitcher /></L> : <LoginRedirect />} />
        <Route path="/app/instagram-bio-generator" element={isAuthenticated ? <L><InstagramBioGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/bio-generator" element={isAuthenticated ? <L><InstagramBioGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/comment-reply-bank" element={isAuthenticated ? <L><CommentReplyBank /></L> : <LoginRedirect />} />
        <Route path="/app/reply-bank" element={isAuthenticated ? <L><CommentReplyBank /></L> : <LoginRedirect />} />
        <Route path="/app/thumbnail-generator" element={isAuthenticated ? <L><YouTubeThumbnailGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/brand-story-builder" element={isAuthenticated ? <L><BrandStoryBuilder /></L> : <LoginRedirect />} />
        <Route path="/app/offer-generator" element={isAuthenticated ? <L><OfferGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/story-hook-generator" element={isAuthenticated ? <L><StoryHookGenerator /></L> : <LoginRedirect />} />
        <Route path="/app/daily-viral-ideas" element={isAuthenticated ? <L><DailyViralIdeas /></L> : <LoginRedirect />} />

        {/* ═══ ADMIN — Entire admin lazy loaded ═══ */}
        <Route path="/app/admin" element={<L><AdminLayout /></L>}>
          <Route index element={<L><AdminDashboard /></L>} />
          <Route path="realtime-analytics" element={<L><RealtimeAnalytics /></L>} />
          <Route path="automation" element={<L><AutomationDashboard /></L>} />
          <Route path="story-video-analytics" element={<L><StoryVideoAnalyticsDashboard /></L>} />
          <Route path="performance" element={<L><AdminMonitoring /></L>} />
          <Route path="login-activity" element={<L><AdminLoginActivity /></L>} />
          <Route path="users" element={<L><AdminUsersManagement /></L>} />
          <Route path="security-reports" element={<L><AdminSecurityReports /></L>} />
          <Route path="security-reports/:report_id" element={<L><AdminSecurityReportDetail /></L>} />
          <Route path="referrals" element={<L><AdminReferrals /></L>} />
          <Route path="reactions" element={<L><AdminReactions /></L>} />
          <Route path="activation" element={<L><AdminActivation /></L>} />
          <Route path="share-links" element={<L><AdminShareLinks /></L>} />
          <Route path="self-healing" element={<L><SelfHealingDashboard /></L>} />
          <Route path="ttfd-analytics" element={<L><TTFDDashboard /></L>} />
          <Route path="user-analytics" element={<L><UserAnalyticsDashboard /></L>} />
          <Route path="security" element={<L><AdminSecurityDashboard /></L>} />
          <Route path="bio-templates" element={<L><BioTemplatesAdmin /></L>} />
          <Route path="workers" element={<L><WorkerDashboard /></L>} />
          <Route path="template-analytics" element={<L><TemplateAnalyticsDashboard /></L>} />
          <Route path="audit-logs" element={<L><AdminAuditLogs /></L>} />
          <Route path="leaderboard" element={<L><TemplateLeaderboard /></L>} />
          <Route path="daily-report" element={<L><DailyReportDashboard /></L>} />
          <Route path="account-locks" element={<L><AccountLockManagement /></L>} />
          <Route path="environment-monitor" element={<L><EnvironmentMonitor /></L>} />
          <Route path="user-activity" element={<L><UserActivityDashboard /></L>} />
          <Route path="system-health" element={<L><SystemHealthDashboard /></L>} />
          <Route path="anti-abuse" element={<L><AntiAbuseDashboard /></L>} />
          <Route path="revenue" element={<L><RevenueAnalyticsDashboard /></L>} />
          <Route path="revenue-analytics" element={<L><RevenueAnalyticsDashboard /></L>} />
          <Route path="retention" element={<L><RetentionDashboard /></L>} />
          <Route path="monitoring" element={<L><MonitoringDashboard /></L>} />
          <Route path="ga4-tester" element={<L><GA4EventTester /></L>} />
          <Route path="feedback" element={<L><AdminFeedbackPage /></L>} />
          <Route path="growth" element={<L><GrowthDashboard /></L>} />
          <Route path="activation-diagnostics" element={<L><ActivationDiagnostics /></L>} />
          <Route path="production-metrics" element={<L><ProductionMetrics /></L>} />
          <Route path="media-security" element={<L><MediaSecurityDashboard /></L>} />
          <Route path="content-engine" element={<L><ContentEngine /></L>} />
          <Route path="payments" element={<L><PaymentsDashboard /></L>} />
          <Route path="conversion" element={<L><ConversionDashboard /></L>} />
          <Route path="photo-trailers" element={<L><PhotoTrailerKpiDashboard /></L>} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<Navigate to={isAuthenticated ? "/app" : "/"} replace />} />
      </Routes>
      </Suspense>

      {/* P0 UI Cleanup Apr 26 2026 — removed: <ResponsiveSupportWrapper />,
          <GuideAssistant />, <PushPrompt />. Founder directive: zero floating
          support clutter. One manual-trigger drawer remains via ActionGuideMount. */}
      {isAuthenticated && <FirstActionOverlay />}
      {isAuthenticated && <PostValueOverlay onTriggerPaywall={triggerPaywall} />}
      <UpgradeModal open={paywallOpen} onClose={() => setPaywallOpen(false)} reason={paywallReason} triggerSource="app" />
      <CookieConsent />
    </ContentProtectionWrapper>
    </ProductGuideProvider>
    </FeedbackProvider>
    </TourProvider>
    </MediaEntitlementProvider>
    </CreditProvider>
    </NotificationProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
